<script setup lang="ts">
// Small settings overlay opened from the gear icon in KomboLogo.vue's top bar (every screen,
// 2026-08-31 round-8 feedback item 23). Holds the voice-commands + semantic-fallback toggles
// (store/settings.ts, localStorage-only device preferences) and — round-9 (2026-08-31) — the
// "Swap sides" action,
// moved here from LiveMatch.vue's body. Same backend guard as before: only meaningful while a
// match is live with 0 points scored (DESIGN.md section 7.4), so the row is disabled otherwise.
import { computed, ref } from 'vue'
import { mdiBugOutline, mdiCloseOctagonOutline, mdiSwapHorizontal, mdiVolumeHigh } from '@mdi/js'
import { settings } from '../store/settings'
import { tableStore } from '../store/table'
import { abandonMatch, swapSides, ApiRequestError } from '../backend'
import { navigate } from '../store/nav'
import { t } from '../i18n'
import ConfirmDialog from './ConfirmDialog.vue'

defineEmits<{ (e: 'close'): void }>()

const swapping = ref(false)
const swapError = ref<string | null>(null)

const liveMatch = computed(() => tableStore.state?.liveMatch ?? null)
const canSwapSides = computed(() => !!liveMatch.value && liveMatch.value.status === 'live' && liveMatch.value.pointsCount === 0)

// Round-12: Abandon match moved here from LiveMatch's top-bar X — the gear is the one
// discrete overflow for match-level actions now. Same confirm + backend call as before.
const abandonConfirmOpen = ref(false)
const abandonError = ref<string | null>(null)

async function onAbandon() {
  abandonConfirmOpen.value = false
  const m = liveMatch.value
  if (!m) return
  try {
    await abandonMatch(m.id)
  } catch (e) {
    abandonError.value = e instanceof ApiRequestError ? e.message : 'Could not abandon the match'
  }
}

async function onSwapSides() {
  if (!liveMatch.value || !canSwapSides.value || swapping.value) return
  swapping.value = true
  swapError.value = null
  try {
    await swapSides(liveMatch.value.id)
  } catch (e) {
    swapError.value = e instanceof ApiRequestError ? e.message : 'Could not swap sides'
  } finally {
    swapping.value = false
  }
}
</script>

<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" @click.self="$emit('close')">
    <div class="w-[420px] max-w-[90%] rounded-2xl bg-neutral-900 border border-neutral-700 p-6 flex flex-col gap-4 shadow-2xl">
      <h2 class="text-xl font-bold">Settings</h2>

      <button
        type="button"
        class="tap-target w-full flex items-center gap-3 rounded-xl bg-neutral-800 active:bg-neutral-700 px-4"
        @click="settings.voiceCommandsEnabled = !settings.voiceCommandsEnabled"
      >
        <span class="flex-1 text-left">
          <span class="block font-semibold">Voice commands (mic)</span>
          <span class="block text-xs text-neutral-500">Lets the kiosk act on spoken score/start/end commands</span>
        </span>
        <!-- Knob is anchored at left-0.5 and slides by translate-x; the ON offset is
             (track 48px) - (2px inset × 2) - (knob 24px) = 20px, so it lands with the same 2px
             inset it starts with instead of poking out of the track (round-9 fix). -->
        <span
          class="w-12 h-7 rounded-full relative shrink-0 transition-colors"
          :class="settings.voiceCommandsEnabled ? 'bg-emerald-600' : 'bg-neutral-700'"
        >
          <span
            class="absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform"
            :class="settings.voiceCommandsEnabled ? 'translate-x-5' : 'translate-x-0'"
          />
        </span>
      </button>

      <!-- Round-24 (2026-08-31): Tier-2 semantic intent fallback (DESIGN.md §11 "Semantic voice
           matching") — a separate, instantly-killable toggle from voice commands themselves, so
           the closed-grammar path can be kept on its own while the "similar phrasing" fallback
           is switched off (or vice versa) if it's ever misbehaving on the floor. -->
      <button
        type="button"
        class="tap-target w-full flex items-center gap-3 rounded-xl bg-neutral-800 active:bg-neutral-700 px-4"
        @click="settings.semanticFallbackEnabled = !settings.semanticFallbackEnabled"
      >
        <span class="flex-1 text-left">
          <span class="block font-semibold">Semantic voice matching</span>
          <span class="block text-xs text-neutral-500">Recognizes similar phrasings ("let's play again") for new match / start / end / corner claims</span>
        </span>
        <span
          class="w-12 h-7 rounded-full relative shrink-0 transition-colors"
          :class="settings.semanticFallbackEnabled ? 'bg-emerald-600' : 'bg-neutral-700'"
        >
          <span
            class="absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform"
            :class="settings.semanticFallbackEnabled ? 'translate-x-5' : 'translate-x-0'"
          />
        </span>
      </button>

      <!-- Round-31 (2026-08-31, DESIGN.md §23) — "Fast mode": trades per-point tracking
           (taps/voice for every point) for entering just each game's final score, so a match
           doesn't have to be interrupted point-by-point. Default ON (settings.fastMode) — the
           user's own framing is that per-point tracking is "a bit awkward", so this is the
           expected common case, not an opt-in extra. es-ES-first label/description per the
           user's explicit request for this one row — every other row on this panel stays
           English, matching the rest of the kiosk (see i18n.ts's header comment). -->
      <button
        type="button"
        class="tap-target w-full flex items-center gap-3 rounded-xl bg-neutral-800 active:bg-neutral-700 px-4"
        @click="settings.fastMode = !settings.fastMode"
      >
        <span class="flex-1 text-left">
          <span class="block font-semibold">{{ t('fastModeLabel') }}</span>
          <span class="block text-xs text-neutral-500">{{ t('fastModeDescription') }}</span>
        </span>
        <span
          class="w-12 h-7 rounded-full relative shrink-0 transition-colors"
          :class="settings.fastMode ? 'bg-emerald-600' : 'bg-neutral-700'"
        >
          <span
            class="absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform"
            :class="settings.fastMode ? 'translate-x-5' : 'translate-x-0'"
          />
        </span>
      </button>

      <button
        type="button"
        class="tap-target w-full flex items-center gap-3 rounded-xl bg-neutral-800 active:bg-neutral-700 px-4 disabled:opacity-30"
        :disabled="!canSwapSides || swapping"
        @click="onSwapSides"
      >
        <span class="flex-1 text-left">
          <span class="block font-semibold">Swap sides</span>
          <span class="block text-xs text-neutral-500">Live match only, before the first point is scored</span>
        </span>
        <svg viewBox="0 0 24 24" class="w-5 h-5 shrink-0 text-neutral-300" fill="currentColor"><path :d="mdiSwapHorizontal" /></svg>
      </button>
      <p v-if="swapError" class="text-sm text-red-400 font-semibold -mt-2">{{ swapError }}</p>

      <button
        v-if="liveMatch && liveMatch.status === 'live'"
        type="button"
        class="tap-target w-full flex items-center gap-3 rounded-xl bg-neutral-800 active:bg-neutral-700 px-4"
        @click="abandonConfirmOpen = true"
      >
        <span class="flex-1 text-left">
          <span class="block font-semibold text-red-400">Abandon match</span>
          <span class="block text-xs text-neutral-500">No rating change will be recorded</span>
        </span>
        <svg viewBox="0 0 24 24" class="w-5 h-5 shrink-0 text-red-400" fill="currentColor"><path :d="mdiCloseOctagonOutline" /></svg>
      </button>
      <p v-if="abandonError" class="text-sm text-red-400 font-semibold -mt-2">{{ abandonError }}</p>

      <!-- Round-10 (2026-08-31): the Debug screen lost its nav tab — it's reachable from here
           instead (developer surface, not something players should see in the tab bar). -->
      <button
        type="button"
        class="tap-target w-full flex items-center gap-3 rounded-xl bg-neutral-800 active:bg-neutral-700 px-4"
        @click="navigate('debug'); $emit('close')"
      >
        <span class="flex-1 text-left">
          <span class="block font-semibold">Debug panel</span>
          <span class="block text-xs text-neutral-500">STT events, scanner console, connection state</span>
        </span>
        <svg viewBox="0 0 24 24" class="w-5 h-5 shrink-0 text-neutral-300" fill="currentColor"><path :d="mdiBugOutline" /></svg>
      </button>

      <!-- Round-30 (2026-09-01): sibling to the Debug panel button above, reached the same way —
           a developer surface, not something players should see in the tab bar. -->
      <button
        type="button"
        class="tap-target w-full flex items-center gap-3 rounded-xl bg-neutral-800 active:bg-neutral-700 px-4"
        @click="navigate('debugTts'); $emit('close')"
      >
        <span class="flex-1 text-left">
          <span class="block font-semibold">TTS debug panel</span>
          <span class="block text-xs text-neutral-500">Voice synth: free text, templates, cache, voice comparison</span>
        </span>
        <svg viewBox="0 0 24 24" class="w-5 h-5 shrink-0 text-neutral-300" fill="currentColor"><path :d="mdiVolumeHigh" /></svg>
      </button>

      <button type="button" class="tap-target px-5 rounded-xl bg-neutral-700 hover:bg-neutral-600 font-semibold self-end" @click="$emit('close')">
        Close
      </button>
    </div>

    <ConfirmDialog
      :open="abandonConfirmOpen"
      title="Abandon this match?"
      body="No rating change will be recorded."
      confirm-label="Abandon"
      danger
      @confirm="onAbandon"
      @cancel="abandonConfirmOpen = false"
    />
  </div>
</template>
